import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { qk } from "@/api/query-keys";
import { notifySuccess } from "@/lib/notify";

import { createModelFile } from "./use-create-model-file";
import { saveModelFile } from "./use-save-model-file";

export type ImportFormFileInput = {
  readonly fileName: string;
  readonly contents: string;
  /** モデルに同じ名前があれば上書き（PUT）、無ければ新規（POST）。 */
  readonly action: "create" | "overwrite";
};

export type ImportFormFilesInput = {
  readonly processModelId: string;
  readonly files: readonly ImportFormFileInput[];
};

/**
 * 利用者が持ち込んだフォーム定義を、選んだ名前のままモデルに置く。
 *
 * 新規は POST、既存は PUT。既存の経路（use-create-model-file / use-save-model-file）と
 * 同じ分け方にそろえる。上書きに楽観ロック（file_contents_hash）は付けない。確認欄で
 * 「上書き」と書かれた行を見て利用者が確定した操作のため（図の取り込みと同じ割り切り）。
 *
 * 1 件ずつ順に送る。途中で失敗したら、入った分は巻き戻さない（戻すと「どれが入って
 * いるか」が余計に分からなくなる）。代わりに、どこまで入ったかと何で止まったかを
 * 持つ失敗にして、画面が確認欄に出せるようにする。
 */
export async function importFormFiles(
  client: ApiClient,
  input: ImportFormFilesInput,
): Promise<readonly string[]> {
  const imported: string[] = [];

  for (const [index, file] of input.files.entries()) {
    try {
      if (file.action === "overwrite") {
        await saveModelFile(client, {
          processModelId: input.processModelId,
          fileName: file.fileName,
          contents: file.contents,
          contentsHash: null,
        });
      } else {
        await createModelFile(client, {
          processModelId: input.processModelId,
          fileName: file.fileName,
          contents: file.contents,
        });
      }
    } catch (cause) {
      throw new ImportFormFilesError({
        imported,
        failed: file.fileName,
        remaining: input.files.slice(index + 1).map((rest) => rest.fileName),
        cause,
      });
    }
    imported.push(file.fileName);
  }

  return imported;
}

/** 途中で止まった取り込み。cause は止まった 1 件の失敗理由。 */
export class ImportFormFilesError extends Error {
  /** 止まるまでに入ったファイル名（順）。 */
  readonly imported: readonly string[];
  /** 失敗したファイル名。 */
  readonly failed: string;
  /** まだ送っていないファイル名（順）。 */
  readonly remaining: readonly string[];
  override readonly cause: unknown;

  constructor(details: {
    readonly imported: readonly string[];
    readonly failed: string;
    readonly remaining: readonly string[];
    readonly cause: unknown;
  }) {
    super(`could not import ${details.failed}`);
    this.name = "ImportFormFilesError";
    this.imported = details.imported;
    this.failed = details.failed;
    this.remaining = details.remaining;
    this.cause = details.cause;
  }
}

/** 失敗は必ず ImportFormFilesError（送信は全部 try の中）。画面はそれだけ扱えばよい。 */
export function useImportFormFiles(): UseMutationResult<
  readonly string[],
  ImportFormFilesError,
  ImportFormFilesInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation<
    readonly string[],
    ImportFormFilesError,
    ImportFormFilesInput
  >({
    mutationFn: (input: ImportFormFilesInput) => importFormFiles(client, input),
    onSuccess: (imported) => {
      notifySuccess(t("toast.formFilesImported", { count: imported.length }));
    },
    // 途中で失敗しても入った分はある。成否によらずファイル一覧を読み直す
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: qk.processModels._root(),
      });
    },
  });
}
